import React, {useCallback, useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {v4 as uuid} from 'uuid';
import {Box, styled} from '@mui/material';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import {
  AssetErrors,
  contentLabActions,
  FileStatus,
  UploadedVideo,
  UploadingFile,
} from '@contentlab/stores/contentLabStore';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import {UploadMode} from '@contentlab/types/UploadMode';
import {Asset} from '@contentlab/types/asset';
import {FeatureName} from '@contentlab/types/project';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets';
import {flowNameForFeature, interactionLocationForFeature} from '../../utils';
import EmptyUpload from './EmptyUpload';
import UploadContent from './UploadContent';
import {onVideoUpload} from './utils';

interface FileUploaderProps {
  feature: FeatureName;
  title: string;
  guideTitle: string;
  guideDescription: string;
  shortenGuideText: string;
  onLearnMoreClick?: () => void;
  renderUploadContent?: (uploadedVideos: UploadedVideo[]) => React.ReactNode;
  onFilesSelected?: (files: File[]) => void;
  onUploadStart?: (uploadingFiles: UploadingFile[]) => void;
  onUploadComplete?: (uploadedVideos: UploadedVideo[]) => void;
  onUploadError?: (
    uniqueAssetId: string,
    file: File,
    errorCodes?: string[]
  ) => void;
  allowMultiUpload?: boolean;
  readyAssets?: Asset[];
  assetErrors: AssetErrors[];
  onFilePickerReady?: (createDropzoneConfig: () => object) => void;
  showUploadErrors?: boolean;
  uploadMode?: UploadMode;
  isLoading?: boolean;
}

const FileUploaderContainer = styled(Box)(() => ({
  width: '100%',
  flex: 1,
  minHeight: 0,
  cursor: 'pointer',
  overflow: 'auto',
  ...scrollbarMixins.overrideScrollbarHidden(),
}));

const DropzoneContainer = styled('div')(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

function FileUploader({
  feature,
  title,
  guideTitle,
  guideDescription,
  shortenGuideText,
  onLearnMoreClick,
  renderUploadContent,
  onFilesSelected,
  onUploadStart,
  onUploadComplete,
  onUploadError,
  allowMultiUpload = true,
  readyAssets = [],
  assetErrors,
  onFilePickerReady,
  showUploadErrors = false,
  uploadMode = UploadMode.MULTIPLE,
  isLoading = false,
}: FileUploaderProps) {
  const {uploadedVideos} = useFeatureAssets(feature);
  const {uiInteraction} = useUiInteractionEvent();
  const processEventHandlers = useProcessEvent();
  const [uploadInteractionId, setUploadInteractionId] = useState(uuid());
  const interactionLocation = interactionLocationForFeature(feature);
  const flowName = flowNameForFeature(feature);

  const handleDropFiles = useCallback(
    async (acceptedFiles: File[]) => {
      await onVideoUpload({
        files: acceptedFiles,
        feature,
        analyticsInteractionId: uploadInteractionId,
        analyticsLocation: interactionLocation,
        processEventHandlers,
        onFilesSelected,
        onUploadStart,
        onUploadComplete,
        onUploadError: showUploadErrors
          ? (uniqueAssetId, file, errorCodes) => {
              onUploadError?.(uniqueAssetId, file, errorCodes);
              if (!errorCodes) {
                return;
              }
              contentLabActions.addAssetErrors([
                {assetId: uniqueAssetId, errorCodes, feature},
              ]);
              contentLabActions.addUploadedVideos([
                {
                  id: uniqueAssetId,
                  isUploading: false,
                  type: 'Uploads',
                  fileName: file.name,
                  durationMillis: 0,
                  aspectRatio: undefined,
                  feature,
                },
              ]);
              contentLabActions.addUploadingFiles([
                {
                  file,
                  id: uniqueAssetId,
                  status: FileStatus.ERROR,
                  progress: 100,
                  error: '',
                  feature,
                  metadata: {
                    duration_millis: 0,
                    width: 0,
                    height: 0,
                    aspect_ratio: null,
                  },
                },
              ]);
            }
          : onUploadError,
      });
      setUploadInteractionId(uuid());
    },
    [
      feature,
      uploadInteractionId,
      interactionLocation,
      processEventHandlers,
      onUploadStart,
      onUploadComplete,
      onUploadError,
      showUploadErrors,
    ]
  );

  const createDropzoneConfig = useCallback(
    () => ({
      onDrop: handleDropFiles,
      accept: {
        'video/mp4': ['.mp4'],
        'video/quicktime': ['.mov'],
        'video/x-msvideo': ['.avi'],
        'video/webm': ['.webm'],
      },
      noClick: false,
      noDrag: false,
      multiple: allowMultiUpload,
      disabled: isLoading,
    }),
    [handleDropFiles, allowMultiUpload, isLoading]
  );

  const {getRootProps, getInputProps, isDragActive} = useDropzone(
    createDropzoneConfig()
  );

  useEffect(() => {
    onFilePickerReady?.(createDropzoneConfig);
  }, [onFilePickerReady, createDropzoneConfig]);

  if (uploadedVideos.length > 0 && renderUploadContent) {
    return renderUploadContent(uploadedVideos);
  }

  const showEmptyUpload =
    uploadedVideos.length === 0 || uploadMode === UploadMode.SINGLE;

  return (
    <FileUploaderContainer>
      <DropzoneContainer
        {...getRootProps({
          onClick: () => {
            uiInteraction({
              item_name: 'browse_computer',
              interaction_location: interactionLocation,
              flow_name: flowName,
              interaction_id: uploadInteractionId,
            });
          },
        })}
        className={`dropzone ${isDragActive ? 'active' : ''}`}
      >
        <input {...getInputProps()} />
        {showEmptyUpload ? (
          <EmptyUpload
            title={title}
            guideTitle={guideTitle}
            guideDescription={guideDescription}
            onLearnMoreClick={onLearnMoreClick}
            assetErrors={uploadMode === UploadMode.SINGLE ? assetErrors : []}
            isLoading={isLoading}
            feature={feature}
          />
        ) : (
          <UploadContent
            feature={feature}
            title={title}
            guideText={shortenGuideText}
            uploadInteractionId={uploadInteractionId}
            readyAssets={readyAssets}
            assetErrors={assetErrors}
            onLearnMoreClick={onLearnMoreClick}
          />
        )}
      </DropzoneContainer>
    </FileUploaderContainer>
  );
}

export default FileUploader;
